import { isDefined } from "@clipboard-health/util-ts";
import { get } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { ExternalIntegrationTypes } from "@src/appV2/ExternalIntegrations/types";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { z } from "zod";

const ukgConfigSchema = z.object({
  ukgBusinessUnitName: z.string().optional(),
});

const externalIntegrationSchema = z.object({
  id: z.string(),
  type: z.nativeEnum(ExternalIntegrationTypes),
  isEnabled: z.boolean(),
  ukg: ukgConfigSchema.optional(),
  ukgReady: z.object({}).passthrough().optional(),
  attendanceOnDemand: z.object({}).passthrough().optional(),
  autumnTrack: z.object({}).passthrough().optional(),
});

export type WorkplaceExternalIntegration = z.infer<typeof externalIntegrationSchema>;

const workplaceExternalIntegrationsResponseSchema = z.object({
  data: z.array(externalIntegrationSchema),
});

interface UseGetWorkplaceExternalIntegrationsParams {
  workplaceId: string;
  enabled?: boolean;
  /**
   * Filter by enabled status. When undefined, passes "all" to return both enabled and disabled integrations.
   * Existing consumers should pass `true` to maintain previous default behavior.
   */
  filterEnabled?: boolean;
}

export function useGetWorkplaceExternalIntegrations(
  params: UseGetWorkplaceExternalIntegrationsParams
): UseQueryResult<WorkplaceExternalIntegration[]> {
  const { workplaceId, enabled, filterEnabled } = params;
  const filterEnabledParameter = isDefined(filterEnabled) ? String(filterEnabled) : "all";

  return useQuery({
    queryKey: ["workplaceExternalIntegrations", workplaceId, filterEnabledParameter],
    enabled,
    queryFn: async () => {
      const response = await get({
        url: `${environmentConfig.REACT_APP_BASE_API_URL}/workplaces/${workplaceId}/external-integrations`,
        queryParams: { "filter[enabled]": filterEnabledParameter },
        responseSchema: workplaceExternalIntegrationsResponseSchema,
      });

      return response.data.data;
    },
  });
}
